import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  SavedView,
  SavedViewConflictProblem,
  SavedViewCreateRequest,
  SavedViewListQuery,
  SavedViewListResponse,
  SavedViewPatchRequest,
} from "./schemas.js";

// The saved-views HTTP surface (spec 16 §5, D-305). Five routes, tagged
// `saved-views`, a platform route group beside `files` and `audit`, because
// every office area saves views into the one table (D-243 ruling 1) and spec 16
// is its home (D-305 ruling 1).
//
// Every handler is session-scoped and names no permission code of its own. A
// view is a member's own resource, and the gate that matters is the screen's:
// the service refuses a `screen_key` whose list route's read code the caller
// lacks, 403 `PERMISSION_DENIED` naming that code. The codes are
// `production.read` for `production.work_orders` and `sales.read` for the five
// sales screens (`SAVED_VIEW_SCREENS` in `./screens.ts`). Since P3-E2 PR E2-1
// an entry may also name `readLineages` (D-314 ruling 6), and the service then
// refuses a caller holding none of them, 403 `PERMISSION_DENIED` with no code
// named: `sales.customers` and `sales.opportunities` name group_admin,
// facility_admin, sales, finance and planner. POST, PATCH and
// DELETE require an `Idempotency-Key`, and D-303 serves a replay only to the
// principal who made the request.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "delete";
  path: string;
  operationId: string;
  summary: string;
  description: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
}): void {
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["saved-views"],
    summary: config.summary,
    description: config.description,
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(config.method !== "get" ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const IdParam = z.object({ id: z.guid() });

const SCREEN_GATE =
  "The screen gate is the list route's read code, `production.read` for `production.work_orders` and `sales.read` for `sales.customers`, `sales.quotes`, `sales.orders`, `sales.opportunities` and `sales.tasks`, and a caller without it is refused 403 `PERMISSION_DENIED` naming the code, so a view's filters never reach a principal who cannot open the list. Where the list route also narrows by lineage, the screen's registry entry names the lineages it admits (`readLineages`, spec 16 §7, D-314 ruling 6), and a caller holding none of them is refused 403 `PERMISSION_DENIED` with no code named: `sales.customers`, `sales.quotes` (since D-337) and `sales.opportunities` admit group_admin, facility_admin, sales, finance and planner (and a tenant's clones of them), so qc and storekeeper, who hold `sales.read`, are refused all three.";

const conflict = (description: string) => ({
  description,
  content: { "application/problem+json": { schema: SavedViewConflictProblem } },
});

route({
  method: "get",
  path: "/v1/saved-views",
  operationId: "listSavedViews",
  summary: "List the saved views of one screen",
  description:
    "Spec 16 §3.12 and §5. The screen's system views, the caller's own views and the tenant's shared views, each with `mutable` (the caller is its author) and `can_moderate` (a shared view that is not a system view, whose author is not the caller, and the caller holds `identity.manage_shared_views` through a group-scoped grant). Both are false on a system view for every reader. `screen_key` is required, and a missing or unknown key answers 400 `VALIDATION_FAILED`. " +
    SCREEN_GATE +
    " Session-scoped, with no permission code of its own.",
  request: { query: SavedViewListQuery },
  responses: {
    200: {
      description: "The views of the screen the caller may apply",
      content: { "application/json": { schema: SavedViewListResponse } },
    },
    400: problemResponse("VALIDATION_FAILED — `screen_key` is missing or names no adopting screen"),
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission` names the screen's read code the caller lacks; or, with no code named, the caller holds none of the screen's `readLineages`",
    ),
  },
});

route({
  method: "get",
  path: "/v1/saved-views/{id}",
  operationId: "getSavedView",
  summary: "One saved view",
  description:
    "Spec 16 §5. A view the caller cannot see answers 404 `NOT_FOUND`, the answer an unknown id gets, so another member's private view does not leak its existence. That check comes first, and the screen gate comes after it. " +
    SCREEN_GATE +
    " Session-scoped, with no permission code of its own.",
  request: { params: IdParam },
  responses: {
    200: {
      description: "The view",
      content: { "application/json": { schema: SavedView } },
    },
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission` names the screen's read code the caller lacks; or, with no code named, the caller holds none of the screen's `readLineages`",
    ),
    404: problemResponse(
      "NOT_FOUND — an unknown id, another member's private view, another tenant's view, or a stored view on a screen this build does not know, indistinguishably",
    ),
  },
});

route({
  method: "post",
  path: "/v1/saved-views",
  operationId: "createSavedView",
  summary: "Save a view",
  description:
    "Spec 16 §3.12 item 3 and §5 (Save as new). The body is validated strictly through a union keyed on `screen_key`, so an unknown key, a config that breaks the named screen's grammar, a column `key` named twice and a config above 16 384 bytes as stored each answer 400 `VALIDATION_FAILED` at the pipe. The view is private unless `shared` is true. Earlier layers answer first. A read-only impersonation session is refused 403 `IMPERSONATION_READ_ONLY`, and a request without an `Idempotency-Key` 400 `IDEMPOTENCY_KEY_REQUIRED`. " +
    SCREEN_GATE +
    " A shared view on `sales.customers` whose `filters.q` reads as a contact address is then refused 400 `VALIDATION_FAILED` at `config.filters.q` with code `contact_address_not_shareable`, before the caps and the name (D-305 ruling 31); a private view keeps its `q`. A name the caller already gives a view on this screen, compared ignoring case, answers 409 `SAVED_VIEW_NAME_TAKEN`, and so does a shared view's name that another shared view on this screen holds. A full cap answers 409 `SAVED_VIEW_LIMIT_REACHED` with the extensions `cap` and `limit`. The service checks the member cap, 20 views of the caller's own on the screen, before the shared cap, 20 shared views in the tenant on the screen, each under a transaction-scoped advisory lock, so a POST that meets both answers `cap: \"member\"`. Appends `identity.saved_view_created` to the audit chain.",
  request: {
    body: { content: { "application/json": { schema: SavedViewCreateRequest } } },
  },
  responses: {
    201: {
      description: "The saved view",
      content: { "application/json": { schema: SavedView } },
    },
    400: problemResponse(
      "VALIDATION_FAILED — at the pipe when the body breaks the named screen's grammar, names a column key twice or holds a NUL character in `name` or `q`, and after the screen gate for a shared `sales.customers` view whose `filters.q` reads as a contact address (D-305 ruling 31), with one field error at `config.filters.q`, code `contact_address_not_shareable` and message key `views.validation.contact_address_not_shareable` / IDEMPOTENCY_KEY_REQUIRED",
    ),
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission` names the screen's read code, or no code is named when the caller holds none of the screen's `readLineages` / IMPERSONATION_READ_ONLY",
    ),
    409: conflict(
      "SAVED_VIEW_NAME_TAKEN / SAVED_VIEW_LIMIT_REACHED — `cap` is `member` or `shared` and `limit` is 20, the member cap checked first",
    ),
  },
});

route({
  method: "patch",
  path: "/v1/saved-views/{id}",
  operationId: "updateSavedView",
  summary: "Rename, re-save, share or unshare the caller's own view",
  description:
    "Spec 16 §3.12 items 3, 4 and 8 and §5. Only the author patches a view. Moderation is a delete, so a moderator's PATCH of another member's view is refused like any other non-author's. Earlier layers answer first. A read-only impersonation session is refused 403 `IMPERSONATION_READ_ONLY`, a request without an `Idempotency-Key` 400 `IDEMPOTENCY_KEY_REQUIRED`, and a body that names none of `name`, `config` or `shared`, or that no screen's grammar accepts, 400 `VALIDATION_FAILED` at the pipe, since the body names no screen and its `config` is checked against the union of the screens' configs. The service's own checks then run in a fixed order. A view the caller cannot see answers 404 `NOT_FOUND` first. A caller without the screen's read code is refused 403 `PERMISSION_DENIED` naming it, and a caller holding none of the screen's `readLineages` is refused 403 `PERMISSION_DENIED` with no code named. A system view answers 409 `SAVED_VIEW_SYSTEM_IMMUTABLE` for everyone, group_admin included. A caller who is not the author is refused 403 `SAVED_VIEW_NOT_OWNER`. A `config` is then validated against the stored row's `screen_key`, and one that screen's grammar refuses answers 400 `VALIDATION_FAILED`, so a work-order config sent to a sales view passes the pipe and is refused there. A `sales.customers` view that would stand shared after the PATCH with a `filters.q` that reads as a contact address is then refused 400 `VALIDATION_FAILED` at `config.filters.q` with code `contact_address_not_shareable`, judged on the stored `config` and `shared` where the body names neither, so sharing a private view that holds an address is refused and a PATCH that unshares it is admitted (D-305 ruling 31). Sharing a view into a full shared cap then answers 409 `SAVED_VIEW_LIMIT_REACHED` with `cap: \"shared\"`, and a PATCH never meets the member cap, since the view already counts against its author's. A name clash answers 409 `SAVED_VIEW_NAME_TAKEN` after that. Appends `identity.saved_view_updated` to the audit chain.",
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: SavedViewPatchRequest } } },
  },
  responses: {
    200: {
      description: "The updated view",
      content: { "application/json": { schema: SavedView } },
    },
    400: problemResponse(
      "VALIDATION_FAILED — at the pipe for a body naming none of `name`, `config` or `shared`, a body no screen accepts or a NUL character in `name` or `q`; after the row checks for a `config` the stored row's screen refuses; and then for a `sales.customers` view that would stand shared after the PATCH, judged on the stored `config` and `shared` where the body names neither, whose `filters.q` reads as a contact address (D-305 ruling 31), with one field error at `config.filters.q`, code `contact_address_not_shareable` and message key `views.validation.contact_address_not_shareable` / IDEMPOTENCY_KEY_REQUIRED",
    ),
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission` names the screen's read code, or no code is named when the caller holds none of the screen's `readLineages` / SAVED_VIEW_NOT_OWNER — the caller is not the author, moderators included / IMPERSONATION_READ_ONLY",
    ),
    404: problemResponse(
      "NOT_FOUND — a view the caller cannot see, or a stored view on a screen this build does not know",
    ),
    409: conflict(
      "SAVED_VIEW_SYSTEM_IMMUTABLE / SAVED_VIEW_NAME_TAKEN / SAVED_VIEW_LIMIT_REACHED — `cap` is `shared` when sharing into a full shared cap",
    ),
  },
});

route({
  method: "delete",
  path: "/v1/saved-views/{id}",
  operationId: "deleteSavedView",
  summary: "Delete a saved view",
  description:
    "Spec 16 §3.12 item 8 and §5. A hard delete. The author deletes their own view. A member who holds `identity.manage_shared_views` through a group-scoped grant may delete any shared view that is not a system view, and a holding of that code at facility or department scope admits nothing. Earlier layers answer first. A read-only impersonation session is refused 403 `IMPERSONATION_READ_ONLY`, and a request without an `Idempotency-Key` 400 `IDEMPOTENCY_KEY_REQUIRED`. The service's own checks then run in a fixed order. A view the caller cannot see answers 404 `NOT_FOUND` first. A caller without the screen's read code is refused 403 `PERMISSION_DENIED` naming it, and a caller holding none of the screen's `readLineages` is refused 403 `PERMISSION_DENIED` with no code named. A system view answers 409 `SAVED_VIEW_SYSTEM_IMMUTABLE` for everyone, group_admin included. A caller who is neither the author nor a moderator deleting a shared view is refused 403 `SAVED_VIEW_NOT_OWNER`, and the view stays. Appends `identity.saved_view_deleted` to the audit chain, its payload saying whether the delete was a moderator's.",
  request: { params: IdParam },
  responses: {
    204: { description: "Deleted" },
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission` names the screen's read code, or no code is named when the caller holds none of the screen's `readLineages` / SAVED_VIEW_NOT_OWNER — neither the author nor a group-scoped moderator deleting a shared view / IMPERSONATION_READ_ONLY",
    ),
    404: problemResponse(
      "NOT_FOUND — a view the caller cannot see, or a stored view on a screen this build does not know",
    ),
    409: problemResponse("SAVED_VIEW_SYSTEM_IMMUTABLE — a system view cannot be deleted"),
  },
});
